(function () {
  const mmDeletedVideosInstances = new WeakMap();
  let mmDeletedVideosActiveInstance = null;

  class MMDeletedVideos {
    constructor(section) {
      this.section = section;
      this.dialog = section.querySelector("[data-mm-deleted-videos-dialog]");
      this.player = section.querySelector("[data-mm-deleted-videos-player]");
      this.youtubeTarget = section.querySelector("[data-mm-deleted-videos-youtube]");
      this.vimeoTarget = section.querySelector("[data-mm-deleted-videos-vimeo]");
      this.hostedTarget = section.querySelector("[data-mm-deleted-videos-hosted-target]");
      this.controls = section.querySelector("[data-mm-deleted-videos-controls]");
      this.lastTrigger = null;
      this.activeVideo = null;
      this.previousBodyOverflow = "";
      this.onClick = this.handleClick.bind(this);
      this.onCancel = this.handleCancel.bind(this);
      this.onVideoPlay = this.handleVideoPlay.bind(this);
      this.onVideoPause = this.handleVideoPause.bind(this);
      this.onVideoEnded = this.handleVideoEnded.bind(this);

      this.init();
    }

    init() {
      this.section.addEventListener("click", this.onClick);
      if (this.dialog) this.dialog.addEventListener("cancel", this.onCancel);
    }

    destroy() {
      this.closeModal(false);
      this.section.removeEventListener("click", this.onClick);
      if (this.dialog) this.dialog.removeEventListener("cancel", this.onCancel);
    }

    handleClick(event) {
      const trigger = event.target.closest("[data-mm-deleted-videos-trigger]");
      const close = event.target.closest("[data-mm-deleted-videos-close]");
      const playToggle = event.target.closest("[data-mm-deleted-videos-play-toggle]");
      const soundToggle = event.target.closest("[data-mm-deleted-videos-sound-toggle]");

      if (trigger && this.section.contains(trigger)) {
        const card = trigger.closest("[data-mm-deleted-videos-card]");
        this.openCard(card, trigger);
        return;
      }

      if (close && this.section.contains(close)) {
        this.closeModal();
        return;
      }

      if (playToggle && this.section.contains(playToggle)) {
        this.toggleHostedPlayback();
        return;
      }

      if (soundToggle && this.section.contains(soundToggle)) {
        this.toggleHostedSound();
        return;
      }

      if (event.target === this.dialog) {
        this.closeModal();
      }
    }

    handleCancel(event) {
      event.preventDefault();
      this.closeModal();
    }

    openCard(card, trigger) {
      if (!card || !this.dialog || !this.player) return;

      const type = card.dataset.mmVideoType;
      if (type !== "hosted" && type !== "youtube" && type !== "vimeo") return;

      if (mmDeletedVideosActiveInstance && mmDeletedVideosActiveInstance !== this) {
        mmDeletedVideosActiveInstance.closeModal(false);
      }

      this.closeMedia();
      this.lastTrigger = trigger;
      mmDeletedVideosActiveInstance = this;

      let hasMedia = false;

      if (type === "hosted") {
        hasMedia = this.openHosted(card);
      } else if (type === "youtube") {
        hasMedia = this.openYouTube(card);
      } else if (type === "vimeo") {
        hasMedia = this.openVimeo(card);
      }

      if (!hasMedia) {
        this.closeMedia();
        this.lastTrigger = null;
        if (mmDeletedVideosActiveInstance === this) mmDeletedVideosActiveInstance = null;
        return;
      }

      this.openDialog();
    }

    openDialog() {
      // this.previousBodyOverflow = document.body.style.overflow;
      // document.body.style.overflow = "hidden";

      if (typeof this.dialog.showModal === "function") {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute("open", "");
      }
    }

    openHosted(card) {
      const template = card.querySelector("[data-mm-deleted-videos-hosted-template]");
      if (!template || !this.hostedTarget) return false;

      const aspectRatio = this.normalizeAspectRatio(card.dataset.mmVideoAspectRatio);
      this.setPlayerAspectRatio(aspectRatio);

      const clone = template.content.cloneNode(true);
      this.hostedTarget.replaceChildren(clone);
      this.activeVideo = this.hostedTarget.querySelector("[data-mm-deleted-videos-hosted]");

      if (!this.activeVideo) return false;

      this.activeVideo.muted = true;
      this.activeVideo.controls = false;
      this.activeVideo.playsInline = true;
      this.activeVideo.preload = "metadata";
      this.activeVideo.addEventListener("play", this.onVideoPlay);
      this.activeVideo.addEventListener("pause", this.onVideoPause);
      this.activeVideo.addEventListener("ended", this.onVideoEnded);

      if (this.controls) this.controls.hidden = false;

      this.updateHostedPlayState(false);
      this.updateHostedSoundState();

      const playPromise = this.activeVideo.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          this.updateHostedPlayState(true);
        });
      }

      return true;
    }

    openYouTube(card) {
      if (!this.youtubeTarget) return false;

      const youtubeInfo = this.getYouTubeInfo(card.dataset.mmYoutubeUrl || "");
      if (!youtubeInfo.id) return false;

      const aspectRatio = youtubeInfo.isShort ? "9 / 16" : "16 / 9";
      this.setPlayerAspectRatio(aspectRatio);

      const iframe = document.createElement("iframe");
      iframe.className = "mm-deleted-videos__youtube-iframe";
      iframe.src = `https://www.youtube.com/embed/${youtubeInfo.id}?autoplay=1&playsinline=1&rel=0`;
      iframe.title = `${card.dataset.mmVideoTitle || "Video"} video player`;
      iframe.allow = "autoplay; encrypted-media; picture-in-picture";
      iframe.allowFullscreen = true;

      this.youtubeTarget.replaceChildren(iframe);
      if (this.controls) this.controls.hidden = true;

      return true;
    }

    openVimeo(card) {
      if (!this.vimeoTarget) return false;

      const vimeoInfo = this.getVimeoInfo(card.dataset.mmVimeoUrl || "");
      if (!vimeoInfo.id) return false;

      this.setPlayerAspectRatio("16 / 9");

      const hashParam = vimeoInfo.hash ? `&h=${vimeoInfo.hash}` : "";
      const iframe = document.createElement("iframe");
      iframe.className = "mm-deleted-videos__youtube-iframe mm-deleted-videos__vimeo-iframe";
      iframe.src = `https://player.vimeo.com/video/${vimeoInfo.id}?autoplay=1&playsinline=1${hashParam}`;
      iframe.title = `${card.dataset.mmVideoTitle || "Video"} video player`;
      iframe.allow = "autoplay; encrypted-media; picture-in-picture; fullscreen";
      iframe.allowFullscreen = true;

      this.vimeoTarget.replaceChildren(iframe);
      if (this.controls) this.controls.hidden = true;

      return true;
    }

    closeModal(restoreFocus = true) {
      this.closeMedia();

      if (this.dialog?.open) {
        this.dialog.close();
      }

      // document.body.style.overflow = this.previousBodyOverflow;

      if (restoreFocus && this.lastTrigger) {
        this.lastTrigger.focus();
      }

      if (mmDeletedVideosActiveInstance === this) {
        mmDeletedVideosActiveInstance = null;
      }

      this.lastTrigger = null;
    }

    closeMedia() {
      if (this.activeVideo) {
        this.activeVideo.pause();
        this.activeVideo.removeEventListener("play", this.onVideoPlay);
        this.activeVideo.removeEventListener("pause", this.onVideoPause);
        this.activeVideo.removeEventListener("ended", this.onVideoEnded);
        this.activeVideo = null;
      }

      if (this.hostedTarget) this.hostedTarget.replaceChildren();
      if (this.youtubeTarget) this.youtubeTarget.replaceChildren();
      if (this.vimeoTarget) this.vimeoTarget.replaceChildren();
      if (this.controls) this.controls.hidden = true;

      this.updateHostedPlayState(true);
      this.updateHostedSoundState();
    }

    toggleHostedPlayback() {
      if (!this.activeVideo) return;

      if (this.activeVideo.paused) {
        this.activeVideo.play();
      } else {
        this.activeVideo.pause();
      }
    }

    toggleHostedSound() {
      if (!this.activeVideo) return;

      this.activeVideo.muted = !this.activeVideo.muted;
      this.updateHostedSoundState();
    }

    handleVideoPlay() {
      this.updateHostedPlayState(false);
      this.updateHostedSoundState();
    }

    handleVideoPause() {
      this.updateHostedPlayState(true);
    }

    handleVideoEnded() {
      this.updateHostedPlayState(true);
    }

    updateHostedPlayState(isPaused) {
      const button = this.section.querySelector("[data-mm-deleted-videos-play-toggle]");
      const playIcon = this.section.querySelector("[data-mm-deleted-videos-play-icon]");
      const pauseIcon = this.section.querySelector("[data-mm-deleted-videos-pause-icon]");

      if (button) button.setAttribute("aria-label", isPaused ? "Play video" : "Pause video");
      if (playIcon) playIcon.hidden = !isPaused;
      if (pauseIcon) pauseIcon.hidden = isPaused;
    }

    updateHostedSoundState() {
      const button = this.section.querySelector("[data-mm-deleted-videos-sound-toggle]");
      if (!button) return;

      const isSoundOn = Boolean(this.activeVideo && !this.activeVideo.muted && this.activeVideo.volume > 0);
      button.classList.toggle("is-sound-on", isSoundOn);
      button.setAttribute("aria-label", isSoundOn ? "Turn sound off" : "Turn sound on");
      button.setAttribute("aria-pressed", String(isSoundOn));
    }

    setPlayerAspectRatio(aspectRatio) {
      if (!this.player) return;

      this.player.style.setProperty("--mm-deleted-videos-player-aspect-ratio", aspectRatio);
      this.player.classList.toggle("is-mm-deleted-videos-portrait", this.isPortraitAspectRatio(aspectRatio));
    }

    normalizeAspectRatio(value) {
      const numericValue = Number(value);

      if (Number.isFinite(numericValue) && numericValue > 0) {
        return String(numericValue);
      }

      if (typeof value === "string" && value.includes("/")) {
        return value;
      }

      return "16 / 9";
    }

    isPortraitAspectRatio(value) {
      const normalizedValue = this.normalizeAspectRatio(value);

      if (normalizedValue.includes("/")) {
        const [width, height] = normalizedValue.split("/").map((part) => Number(part.trim()));
        return Number.isFinite(width) && Number.isFinite(height) && width < height;
      }

      const numericValue = Number(normalizedValue);
      return Number.isFinite(numericValue) && numericValue < 1;
    }

    getYouTubeInfo(url) {
      if (!url) return { id: "", isShort: false };

      try {
        const parsedUrl = new URL(url);
        const pathname = parsedUrl.pathname;

        if (parsedUrl.hostname.includes("youtu.be")) {
          return { id: pathname.replace("/", "").split("/")[0], isShort: false };
        }

        if (parsedUrl.searchParams.get("v")) {
          return { id: parsedUrl.searchParams.get("v"), isShort: false };
        }

        const embedMatch = pathname.match(/\/embed\/([^/?]+)/);
        if (embedMatch) return { id: embedMatch[1], isShort: false };

        const shortsMatch = pathname.match(/\/shorts\/([^/?]+)/);
        if (shortsMatch) return { id: shortsMatch[1], isShort: true };
      } catch (error) {
        return { id: "", isShort: false };
      }

      return { id: "", isShort: false };
    }

    getVimeoInfo(url) {
      if (!url) return { id: "", hash: "" };

      try {
        const parsedUrl = new URL(url);
        const segments = parsedUrl.pathname.split("/").filter(Boolean);
        const idIndex = segments.findIndex((segment) => /^\d+$/.test(segment));
        if (idIndex === -1) return { id: "", hash: "" };

        const id = segments[idIndex];
        let hash = parsedUrl.searchParams.get("h") || "";

        const nextSegment = segments[idIndex + 1];
        if (!hash && nextSegment && /^[a-z0-9]+$/i.test(nextSegment)) {
          hash = nextSegment;
        }

        return { id, hash };
      } catch (error) {
        return { id: "", hash: "" };
      }
    }
  }

  function initMMDeletedVideos(container = document) {
    container.querySelectorAll("[data-mm-deleted-videos-section]").forEach((section) => {
      if (mmDeletedVideosInstances.has(section)) return;
      mmDeletedVideosInstances.set(section, new MMDeletedVideos(section));
    });
  }

  function unloadMMDeletedVideos(section) {
    const instance = mmDeletedVideosInstances.get(section);
    if (!instance) return;

    instance.destroy();
    mmDeletedVideosInstances.delete(section);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initMMDeletedVideos());
  } else {
    initMMDeletedVideos();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initMMDeletedVideos(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll("[data-mm-deleted-videos-section]").forEach((section) => {
      unloadMMDeletedVideos(section);
    });
  });
})();